import type { TripPrice } from './types';

/**
 * The airline's price carried by a transport view from operator-bff
 * (`operator-bff:airline-money@v2`, commercial-contract P5) — a trip, a shuttle run,
 * a reservations-desk trip or its vendor snapshot:
 *
 *  - `undefined` — the view carries no amount (the vendor has written none): show nothing;
 *  - `null`      — the BFF could not price it (no price card): show "not available";
 *  - a price     — what the airline pays.
 *
 * A `null` price means "not available" only when the payload says it was NOT priced
 * (`priced: false` on the view, or passed in when the flag rides on the envelope): the
 * reservations-desk trip also sends `price: null` while the vendor has quoted no fare,
 * which is nothing to show. Either way no amount is ever invented.
 *
 * Structural on purpose: those views are typed in different modules, and this reads
 * ONLY the `price` key — a `cost` (Nexa's) is never read, whatever a stale cache holds.
 */
export function priceOfView(
  view: object | null | undefined,
  /** The envelope's `priced` flag, when the view itself does not carry one. */
  envelopePriced?: boolean,
): TripPrice | null | undefined {
  if (!view || !('price' in view)) return undefined;
  const price = (view as { price?: unknown }).price;
  if (price === null) {
    const own = (view as { priced?: unknown }).priced;
    const priced = typeof own === 'boolean' ? own : envelopePriced;
    return priced === false ? null : undefined;
  }
  if (
    price &&
    typeof price === 'object' &&
    typeof (price as TripPrice).amount === 'number' &&
    Number.isFinite((price as TripPrice).amount) &&
    typeof (price as TripPrice).currency === 'string'
  ) {
    return price as TripPrice;
  }
  return undefined;
}

/** "US$ 20.24" in the operator's language; a bad currency code degrades to "20.24 XYZ". */
export function formatTripPrice(price: TripPrice, language?: string): string {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency: price.currency }).format(price.amount);
  } catch {
    return `${price.amount.toFixed(2)} ${price.currency}`;
  }
}
